// A colour per festival, and the names the map and the panels look them up by.
//
// Its own module, importing nothing, for the same reason feed-health.ts is one:
// three files need this table — the map sprites in icons.ts, the row glyphs in
// layer-icons.ts, and the replaces-a-disc list in map-style.ts — and none of
// them should have to import a canvas renderer to find out what colour the
// Tattoo is.
//
// ── Why thirteen colours and not one ────────────────────────────────────────
//
// The layer used to be the Fringe alone, so one pink star was the whole
// vocabulary. It is now one layer per festival, and in August five of them run
// at once over the same square mile — a reader looking at two stars outside the
// same building should be able to tell the Tattoo from the Fringe without
// clicking either.
//
// The star stays the same shape throughout, deliberately. Shape says "a venue
// with something on tonight", which is true of all of them; colour says which
// festival. Giving each festival its own silhouette would trade a distinction
// worth having for one nobody asked for, and thirteen glyphs at nineteen pixels
// collapse into the same blob anyway — the argument the star was chosen on.
//
// The August five are picked to sit as far apart as the wheel allows — pink,
// gold, blue, teal, orange — because those are the ones that will be on screen
// together. The rest fill in around them; two similar ones are only a problem
// on a day both are running, and Hogmanay is not on during the Book Festival.

/**
 * Festival id to pin colour.
 *
 * Keyed by the API's `festival_id`, so it matches FESTIVALS in the backend's
 * providers/festivals.ts one for one. A festival missing from here still draws
 * — see festivalColour() — it just draws in the fallback, which is the old
 * Fringe pink.
 */
export const FESTIVAL_COLOURS: Readonly<Record<string, string>> = {
  /** Unchanged, and not up for changing: the Fringe star has been pink since the layer existed. */
  fringe: '#ff5c8a',
  tattoo: '#ffd23f',
  book: '#4aa3df',
  international: '#2fd4c4',
  art: '#ff8c1a',
  jazz: '#9b7cf0',
  science: '#7ed957',
  imaginate: '#ff6f3c',
  film: '#c0c6cc',
  hogmanay: '#e2574c',
  storytelling: '#8fbf6f',
  mela: '#f05fd0',
  /** The demo feed an unapproved key sees. Grey, because it is not a festival. */
  demofringe: '#8b8f96',
};

/** The fallback, for a festival id the API adds before this table hears about it. */
export const FESTIVAL_FALLBACK = '#ff5c8a';

export function festivalColour(id: string): string {
  return FESTIVAL_COLOURS[id] ?? FESTIVAL_FALLBACK;
}

/**
 * The sprite name a feature of this festival carries, matching the backend.
 *
 * The backend writes `icon: festival-<id>` on every venue feature; this is the
 * one place the two spellings are tied together, so a change here is a change
 * there and the grep finds both.
 */
export function festivalIconName(id: string): string {
  return `festival-${id}`;
}

/** The provider id the backend gives a festival's layer: `festival:<id>`. */
export function festivalLayerId(id: string): string {
  return `festival:${id}`;
}

/** Every sprite name the festivals can ask the map for. */
export function festivalIconNames(): string[] {
  return Object.keys(FESTIVAL_COLOURS).map(festivalIconName);
}
